/**
 * Multi-Party Signature Embedding Utility
 * Embeds multiple signatures into HTML documents progressively
 * Matches signatures to placeholders via data-signer-id attributes
 */

export interface SignatureData {
  role: string;
  signer_id: string;
  signature_data: string; // base64 data URI
  signed_at: string;
}

/**
 * Embed multiple signatures into HTML document progressively
 * 
 * Algorithm:
 * 1. For each signature in the array:
 *    a. Find the div with matching data-signer-id attribute
 *    b. Replace its content with signature image + timestamp
 *    c. Leave other signature placeholders untouched
 * 2. Replace date placeholders if finalizedAt provided
 * 
 * @param originalHtml - The HTML template with signature placeholders
 * @param signatures - Array of signatures to embed
 * @param finalizedAt - Optional finalization date for document
 * @returns Modified HTML with signatures embedded
 */
export async function embedSignaturesIntoHtml(
  originalHtml: string,
  signatures: SignatureData[],
  finalizedAt?: string
): Promise<string> {
  let updatedHtml = originalHtml;

  // Process each signature
  for (const signature of signatures) {
    // Build signature image element
    const signatureImage = `<img src="${signature.signature_data}" style="max-width: 3.5in; height: auto; display: block; margin-bottom: 0.25rem;" alt="Signature" />`;
    
    // Build timestamp element
    const timestamp = new Date(signature.signed_at).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
    const timestampElement = `<div style="font-size: 0.875rem; color: #666; margin-top: 0.25rem;">Signed: ${timestamp}</div>`;

    // Build complete signature block
    const signatureBlock = `${signatureImage}${timestampElement}`;

    // Find and replace the matching signature placeholder
    // Pattern: <div ... data-signer-id="[signer_id]" ...>...</div>
    const escapedSignerId = signature.signer_id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    // Regex to match the entire div with data-signer-id attribute
    // This matches the opening tag, content, and closing tag
    const signaturePlaceholderRegex = new RegExp(
      `<div([^>]*data-signer-id="${escapedSignerId}"[^>]*)>([\\s\\S]*?)<\\/div>`,
      'gi' // Case insensitive and global
    );

    // Replace only if we find a match
    const matchFound = signaturePlaceholderRegex.test(updatedHtml);
    if (matchFound) {
      // Reset regex lastIndex for actual replacement
      signaturePlaceholderRegex.lastIndex = 0;
      updatedHtml = updatedHtml.replace(
        signaturePlaceholderRegex,
        `<div$1>${signatureBlock}</div>`
      );
      console.log(`✅ Embedded signature for signer_id: ${signature.signer_id}`);
    } else {
      console.warn(`⚠️  No signature placeholder found for signer_id: ${signature.signer_id}`);
      console.log(`   Searched HTML length: ${updatedHtml.length} chars`);
      console.log(`   Looking for data-signer-id="${signature.signer_id}"`);
    }
  }

  // Replace date placeholders with finalized date if provided
  if (finalizedAt) {
    const formattedDate = new Date(finalizedAt).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });

    // Replace various date placeholder patterns
    updatedHtml = updatedHtml.replace(
      /Date:\s*<div class="date-line"><\/div>/g,
      `Date: ${formattedDate}`
    );
    updatedHtml = updatedHtml.replace(
      /Date:\s*<span[^>]*><\/span>/g,
      `Date: ${formattedDate}`
    );
    updatedHtml = updatedHtml.replace(
      /Date:\s*_{3,}/g,
      `Date: ${formattedDate}`
    );
  }

  return updatedHtml;
}

/**
 * Helper function to extract all signature placeholders from HTML
 * Useful for debugging and validation
 */
export function extractSignaturePlaceholders(html: string): Array<{
  role: string | null;
  signer_id: string | null;
  element: string;
}> {
  const placeholders: Array<{
    role: string | null;
    signer_id: string | null;
    element: string;
  }> = [];

  // Match all divs with data-signature-role or data-signer-id
  const placeholderRegex = /<div[^>]*(?:data-signature-role|data-signer-id)[^>]*>[\s\S]*?<\/div>/gi;
  const matches = html.match(placeholderRegex);

  if (matches) {
    matches.forEach(match => {
      // Extract data-signature-role
      const roleMatch = match.match(/data-signature-role="([^"]+)"/i);
      const role = roleMatch ? roleMatch[1] : null;

      // Extract data-signer-id
      const signerIdMatch = match.match(/data-signer-id="([^"]+)"/i);
      const signer_id = signerIdMatch ? signerIdMatch[1] : null;

      placeholders.push({
        role,
        signer_id,
        element: match
      });
    });
  }

  return placeholders;
}

/**
 * Validate that HTML has required signature placeholders
 */
export function validateSignaturePlaceholders(
  html: string,
  requiredSignerIds: string[]
): {
  valid: boolean;
  missing: string[];
  found: string[];
} {
  const placeholders = extractSignaturePlaceholders(html);
  const foundSignerIds = placeholders
    .map(p => p.signer_id)
    .filter((id): id is string => id !== null);

  const missing = requiredSignerIds.filter(id => !foundSignerIds.includes(id));

  return {
    valid: missing.length === 0,
    missing,
    found: foundSignerIds
  };
}
